import { useRef, type MouseEvent, type ReactNode } from "react";
import { clsx } from "clsx";
import { Button } from "@/ui/components/Button";
import { Icon } from "@/ui/icons/Icon";
import styles from "./TiltedOfferCard.module.scss";

export type TiltedOfferGlow =
  | "credit-line"
  | "installment"
  | "volt-zero"
  | "volt-one"
  | "volt-infinite";

interface TiltedOfferCardProps {
  title: string;
  description: [string, string];
  glow: TiltedOfferGlow;
  // The product's card face, laid at the side.
  face: ReactNode;
  faceVariant: "ocean" | "volt";
  trackingId: string;
  onDetailsClick: () => void;
}

// An offer described on the left, its card tilted at the side.
export function TiltedOfferCard({
  title,
  description,
  glow,
  face,
  faceVariant,
  trackingId,
  onDetailsClick,
}: TiltedOfferCardProps) {
  const detailsRef = useRef<HTMLButtonElement>(null);

  // The whole card opens the details, through its button: one tracked click,
  // and the button stays what keyboards and screen readers reach.
  const handleCardClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!detailsRef.current?.contains(event.target as Node)) {
      detailsRef.current?.click();
    }
  };

  return (
    <div className={styles.root} onClick={handleCardClick}>
      <div className={clsx(styles.glow, styles[`glow--${glow}`])} aria-hidden />
      <div className={clsx(styles.face, styles[`face--${faceVariant}`])}>
        {face}
      </div>

      <div className={styles.content}>
        <div className={styles.text}>
          <p className={styles.title}>{title}</p>
          <p className={styles.description}>
            {description[0]}
            <br />
            {description[1]}
          </p>
        </div>
        <Button
          ref={detailsRef}
          trackingId={trackingId}
          variant="text"
          size="xsmall"
          text="Află detalii"
          rightIcon={<Icon name="arrow-right" />}
          onClick={onDetailsClick}
          className={styles.details}
        />
      </div>
    </div>
  );
}
